import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { styles } from './adminStyles';
import { OUTCOME_TYPES } from '../../../config/outcomeTypes';

// {COEFFICIENT} is universal (not a GIS layer lookup) so it's always offered
// alongside whatever GIS tokens GET /algorithms/gis-tokens returns.
const STATIC_TOKEN_CHIPS = [{ token: 'COEFFICIENT', label: 'Coefficient', layerName: '', description: 'The algorithm\'s own coefficient value.' }];

// Mirrors backend/utils/formulaTokens.js's validateFormulaBrackets — strips
// well-formed {TOKEN} placeholders, then requires the remainder to be pure
// arithmetic. Case-insensitive to match the backend's tokenizer, which
// normalizes {gis_soil_type}/{GIS_SOIL_TYPE} to the same uppercase key. Kept
// in sync manually since frontend/backend don't share code.
const BRACKETED_TOKEN_RE = /\{[A-Za-z0-9_]+\}/g;
const ARITHMETIC_ONLY_RE = /^[\s\d.+\-*/()]*$/;
function validateFormulaBrackets(formulaString) {
  if (!formulaString) return null;
  const stripped = formulaString.replace(BRACKETED_TOKEN_RE, '');
  if (!ARITHMETIC_ONLY_RE.test(stripped)) {
    return 'All formula variables must be enclosed in curly brackets, e.g., {VARIABLE_NAME}';
  }
  return null;
}

// Exported (not just used internally by AlgorithmLibrary) so ActionModal.js
// can reuse it for the "+ Create new algorithm" inline flow in the Outcomes
// Delivered section, instead of duplicating the formula-builder UI.
export const AlgorithmModal = ({ existingAlgorithm, onClose, onSave, initialOutcomeType }) => {
  const [name, setName] = useState(existingAlgorithm?.name || '');
  const [outcomeType, setOutcomeType] = useState(existingAlgorithm?.outcomeType || initialOutcomeType || OUTCOME_TYPES[0]);
  const [coefficient, setCoefficient] = useState(existingAlgorithm?.coefficient ?? '');
  const [formulaString, setFormulaString] = useState(existingAlgorithm?.formulaString || '');
  const [gisTokens, setGisTokens] = useState([]);
  const [formulaError, setFormulaError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get('/algorithms/gis-tokens')
      .then(res => { if (!cancelled) setGisTokens(res.data || []); })
      .catch(() => { if (!cancelled) setGisTokens([]); });
    return () => { cancelled = true; };
  }, []);

  const tokenChips = [...STATIC_TOKEN_CHIPS, ...gisTokens];

  const handleSubmit = (e) => {
    e.preventDefault();
    const error = validateFormulaBrackets(formulaString);
    if (error) { setFormulaError(error); return; }
    setFormulaError('');
    onSave({ name, outcomeType, coefficient: parseFloat(coefficient), formulaString });
  };

  const insertToken = (token) => {
    setFormulaString(prev => prev ? `${prev} {${token}}` : `{${token}}`);
  };

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, width: '500px', height: 'auto', padding: '25px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, ...styles.h2 }}>{existingAlgorithm ? 'Edit Outcome Algorithm' : 'New Outcome Algorithm'}</h2>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#475569' }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <label style={styles.label}>Name</label>
          <input style={styles.input} value={name} onChange={(e) => setName(e.target.value)} required />

          <label style={styles.label}>Outcome Type</label>
          <select style={styles.select} value={outcomeType} onChange={(e) => setOutcomeType(e.target.value)}>
            {OUTCOME_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <label style={styles.label}>Coefficient</label>
          <input style={styles.input} type="number" step="0.0001" value={coefficient} onChange={(e) => setCoefficient(e.target.value)} required />

          <label style={styles.label}>Formula</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>
            {tokenChips.map(({ token, label, layerName, description }) => (
              <button
                key={token}
                type="button"
                title={description || undefined}
                onClick={() => insertToken(token)}
                style={{ padding: '3px 8px', border: '1px solid #CBD5E1', backgroundColor: '#F8FAFC', color: '#0D9488', borderRadius: '4px', cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.75rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}
              >
                <span>{`{${token}}`}</span>
                {(label || layerName) && (
                  <span style={{ fontFamily: '"Inter", sans-serif', fontSize: '0.65rem', color: '#64748B' }}>
                    {label}{layerName ? ` (${layerName})` : ''}
                  </span>
                )}
              </button>
            ))}
          </div>
          <textarea
            style={{ ...styles.input, height: '80px', resize: 'vertical', fontFamily: 'monospace' }}
            placeholder="e.g. {COEFFICIENT} * {GIS_SOIL_TYPE}"
            value={formulaString}
            onChange={(e) => { setFormulaString(e.target.value); if (formulaError) setFormulaError(''); }}
          />
          <div style={{ fontSize: '0.75rem', color: '#94A3B8', marginTop: '5px', marginBottom: '15px' }}>
            This formula calculates the <strong>per-unit outcome rate</strong> (e.g. impact per ha/meter) — the
            evaluation engine multiplies the result by the bid quantity, so do not multiply by area/quantity yourself.
            Use {'{TOKEN}'} placeholders — any {'{GIS_*}'} token is auto-registered as a required GIS layer on save.
          </div>
          {formulaError && (
            <div style={{ fontSize: '0.8rem', color: '#DC2626', fontWeight: 'bold', marginTop: '-10px', marginBottom: '15px' }}>
              {formulaError}
            </div>
          )}

          <button type="submit" style={styles.submitBtn}>Save Algorithm</button>
        </form>
      </div>
    </div>
  );
};

const CsvUploadSection = ({ onUpload }) => {
  const [file, setFile] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [rowErrors, setRowErrors] = useState([]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setIsBusy(true); setStatus(''); setRowErrors([]);
    try {
      const result = await onUpload(file);
      setStatus('✅ ' + (result?.msg || 'Imported successfully.'));
      setFile(null);
      setTimeout(() => setStatus(''), 4000);
    } catch (err) {
      setStatus('❌ Upload Failed: ' + (err.response?.data?.msg || 'Error'));
      setRowErrors(err.response?.data?.errors || []);
    } finally { setIsBusy(false); }
  };

  return (
    <div style={{ ...styles.card }}>
      <h3 style={{ marginTop: 0, ...styles.h3 }}>Bulk Import via CSV</h3>
      <div style={styles.instructionBox}>
        Columns: Outcome_Name, Outcome_Type, Outcome_Coefficient, Formula (<strong>Required</strong>), plus any optional GIS_* columns. All-or-nothing — the whole file is rejected if any row fails validation.
      </div>
      <form onSubmit={handleUpload} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <input disabled={isBusy} type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} style={{ fontFamily: '"Inter", sans-serif', fontSize: '0.85rem' }} />
        <button
          disabled={isBusy || !file}
          type="submit"
          style={{ ...styles.btnPrimary, opacity: (isBusy || !file) ? 0.6 : 1, cursor: (isBusy || !file) ? 'not-allowed' : 'pointer' }}
        >
          {isBusy ? 'Uploading...' : 'Upload CSV'}
        </button>
      </form>
      {status && <div style={{ marginTop: '10px', fontSize: '0.85rem', fontWeight: 'bold', color: status.includes('Failed') ? '#F59E0B' : '#0D9488' }}>{status}</div>}
      {rowErrors.length > 0 && (
        <ul style={{ marginTop: '10px', fontSize: '0.8rem', color: '#F59E0B' }}>
          {rowErrors.map((err, i) => <li key={i}>{err}</li>)}
        </ul>
      )}
    </div>
  );
};

const AlgorithmLibrary = ({
  algorithms,
  isAlgorithmModalOpen, setIsAlgorithmModalOpen, editingAlgorithm, setEditingAlgorithm,
  handleSaveAlgorithm, handleArchiveToggleAlgorithm, handleUploadAlgorithmsCsv, handleDeleteAlgorithm
}) => {
  const [showArchivedView, setShowArchivedView] = useState(false);

  // Legacy snapshots (pre-edit clones — see routes/algorithms.js's PUT /:id)
  // are folded into the Archived tab rather than given their own tab: they're
  // audit-only records, never a live editing target, so they belong wherever
  // "not currently in use" algorithms are already reviewed.
  const visibleAlgorithms = (algorithms || []).filter(a => showArchivedView ? (a.status === 'archived' || a.status === 'legacy') : a.status === 'active');

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <CsvUploadSection onUpload={handleUploadAlgorithmsCsv} />
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, ...styles.h2 }}>Outcome Algorithms</h2>
            <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem' }}>Manage the scoring algorithms bound to Action outcomes.</p>
          </div>
          <button
            onClick={() => { setEditingAlgorithm(null); setIsAlgorithmModalOpen(true); }}
            style={styles.btnPrimary}
          >
            + New Algorithm
          </button>
        </div>

        <div style={{ display: 'flex', gap: '30px', borderBottom: '2px solid #ddd', marginBottom: '20px' }}>
          <button
            onClick={() => setShowArchivedView(false)}
            style={{ background: 'none', border: 'none', borderBottom: !showArchivedView ? '3px solid #0D9488' : '3px solid transparent', color: !showArchivedView ? '#0F172A' : '#475569', fontWeight: 'bold', padding: '10px 5px', fontSize: '1rem', cursor: 'pointer', fontFamily: '"Inter", sans-serif', transform: 'translateY(2px)' }}
          >
            Active Algorithms
          </button>
          <button
            onClick={() => setShowArchivedView(true)}
            style={{ background: 'none', border: 'none', borderBottom: showArchivedView ? '3px solid #0D9488' : '3px solid transparent', color: showArchivedView ? '#0F172A' : '#475569', fontWeight: 'bold', padding: '10px 5px', fontSize: '1rem', cursor: 'pointer', fontFamily: '"Inter", sans-serif', transform: 'translateY(2px)' }}
          >
            Archived / Legacy
          </button>
        </div>

        <div style={{ overflowX: 'auto', backgroundColor: '#FFFFFF', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
          <table style={{ ...styles.table, width: '100%' }}>
            <thead>
              <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                <th style={{ ...styles.th, textAlign: 'left' }}>Name</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Outcome Type</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Coefficient</th>
                <th style={{ ...styles.th, textAlign: 'left' }}>Formula</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Status</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Update</th>
              </tr>
            </thead>
            <tbody>
              {visibleAlgorithms.map(algorithm => (
                <tr key={algorithm._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  <td style={{ ...styles.td, fontWeight: 'bold', color: '#0F172A', textAlign: 'left' }}>{algorithm.name}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{algorithm.outcomeType}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{algorithm.coefficient}</td>
                  <td style={{ ...styles.td, textAlign: 'left', fontFamily: 'monospace', fontSize: '0.8rem', color: '#475569' }}>
                    {algorithm.formulaString || <span style={{ fontStyle: 'italic', color: '#94A3B8', fontFamily: '"Inter", sans-serif' }}>None</span>}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span style={{ backgroundColor: algorithm.status === 'active' ? '#E0F2FE' : '#F1F5F9', color: algorithm.status === 'active' ? '#0369A1' : '#475569', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold' }}>
                      {algorithm.status.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {algorithm.status !== 'legacy' && (
                      <button
                        onClick={() => { setEditingAlgorithm(algorithm); setIsAlgorithmModalOpen(true); }}
                        style={{ padding: '6px 12px', border: 'none', backgroundColor: '#0D9488', color: '#FFFFFF', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', marginRight: '10px' }}
                      >
                        Edit
                      </button>
                    )}
                    {algorithm.status === 'archived' && (
                      <button
                        onClick={() => handleArchiveToggleAlgorithm(algorithm._id, false)}
                        style={{ padding: '6px 12px', border: 'none', backgroundColor: '#0D9488', color: '#FFFFFF', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', marginRight: '10px' }}
                      >
                        Restore
                      </button>
                    )}
                    {algorithm.status === 'active' && (
                      <button
                        onClick={() => handleArchiveToggleAlgorithm(algorithm._id, true)}
                        style={{ padding: '6px 12px', border: '1px solid #0D9488', backgroundColor: '#FFFFFF', color: '#0D9488', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', marginRight: '10px' }}
                      >
                        Archive
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (window.confirm(`Permanently delete "${algorithm.name}"? This cannot be undone.`)) {
                          handleDeleteAlgorithm(algorithm._id);
                        }
                      }}
                      style={{ padding: '6px 12px', border: '1px solid #DC2626', backgroundColor: '#FFFFFF', color: '#DC2626', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {visibleAlgorithms.length === 0 && (
                <tr><td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#64748B' }}>
                  {showArchivedView ? 'No archived or legacy algorithms.' : 'No outcome algorithms found.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isAlgorithmModalOpen && (
        <AlgorithmModal
          existingAlgorithm={editingAlgorithm}
          onClose={() => { setIsAlgorithmModalOpen(false); setEditingAlgorithm(null); }}
          onSave={handleSaveAlgorithm}
        />
      )}
    </div>
  );
};

export default AlgorithmLibrary;
